import { useEffect, useRef } from 'react'
import { SignUp, useUser } from '@clerk/react'
import { CURRENT_TERMS_VERSION } from '@spoofio/shared'
import { AuthLayout } from '@/components/branding/AuthLayout'
import { clerkAppearance } from '@/components/branding/clerkAppearance'

/**
 * SignupPage MUST remain a pure render — see commits 119ddce, c11f26d,
 * fc672b0. Adding state or effects to this component remounts <SignUp>,
 * which causes Clerk to send duplicate verification emails. Any side
 * effects (e.g. recording terms acceptance) must live in a sibling child
 * component that is isolated from the <SignUp> subtree.
 *
 * The `appearance` prop must reference a stable module-level constant
 * (see clerkAppearance.ts) — passing a fresh object on every render also
 * triggers the remount-and-duplicate-email failure mode.
 */
export function SignupPage() {
  return (
    <>
      <AuthLayout
        formTitle="Create your account"
        formSubtitle="Free 14-day trial. No credit card required."
        switchPrompt="Already have an account?"
        switchLinkText="Sign in"
        switchLinkHref="/login"
        showTermsFooter
      >
        <SignUp
          signInUrl="/login"
          fallbackRedirectUrl="/"
          appearance={clerkAppearance}
        />
      </AuthLayout>

      <TermsAcceptanceTracker />
    </>
  )
}

/**
 * Sibling-only side effect: once Clerk reports a freshly signed-up user,
 * persist their terms acceptance into Clerk `unsafeMetadata`. The Clerk
 * webhook reads this on `user.created` and writes `terms_version` to D1.
 *
 * Lives in its own component so its state changes never re-render the
 * <SignUp> subtree above (see SignupPage doc comment).
 */
function TermsAcceptanceTracker() {
  const { isLoaded, isSignedIn, user } = useUser()
  const writtenRef = useRef(false)

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    if (writtenRef.current) return
    const meta = user.unsafeMetadata as Record<string, unknown>
    if (meta.termsAccepted === true) {
      writtenRef.current = true
      return
    }
    writtenRef.current = true
    void user.update({
      unsafeMetadata: {
        ...meta,
        termsAccepted: true,
        termsVersion: CURRENT_TERMS_VERSION,
        termsAcceptedAt: new Date().toISOString(),
      },
    })
  }, [isLoaded, isSignedIn, user])

  return null
}
